import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.2 Tier 1 — schedule-side slide-over listing every task in
// the current schedule that funds a budget line, with quick-unlink
// buttons. Doesn't touch the Bryntum Gantt widget: the panel opens
// from a toolbar button and reads a lightweight JSON endpoint so
// future Bryntum updates (new versions, custom columns) don't break
// this surface.
//
// A future tier can add an in-Gantt badge column by teaching
// gantt_controller.js about a `linkedBudgetCount` per task — for
// Tier 1 the side panel is the whole schedule-side affordance.
export default class extends Controller {
  static targets = ["panel", "body", "badge"]
  static values = {
    scheduleId: Number,
    projectId: Number,
    linksUrl: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    // Prime the toolbar badge so users can see at a glance whether
    // the schedule has any budget links without opening the panel.
    this._refreshBadge()
  }

  async open(event) {
    event?.preventDefault?.()
    this._show(this.panelTarget)
    this._track("EVA Schedule Panel Opened")
    await this._loadAndRender()
  }

  close() {
    this._hide(this.panelTarget)
  }

  stopPropagation(event) { event.stopPropagation() }

  async removeLink(event) {
    const { linkIdParam } = event.currentTarget.dataset
    const id = parseInt(linkIdParam, 10)
    const resp = await this._ajaxJSON("DELETE", `/eva_links/${id}`)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to unlink", "error")
      return
    }
    this._track("EVA Link Removed", { link_id: id, origin: "schedule_panel" })
    this._showToast("Task unlinked from budget line")
    await this._loadAndRender()
  }

  // ── Rendering ──────────────────────────────────────────────────────

  async _loadAndRender() {
    const resp = await this._ajaxJSON("GET", this.linksUrlValue)
    if (!resp.ok) {
      this.bodyTarget.innerHTML = `<div class="text-red-600">${this._escape(resp.body?.error || "Failed to load links")}</div>`
      return
    }
    this._renderBody(resp.body)
    this._updateBadgeCount(resp.body?.tasks?.length || 0)
  }

  _renderBody(payload) {
    const tasks = payload?.tasks || []
    if (tasks.length === 0) {
      this.bodyTarget.innerHTML = `
        <div class="text-gray-500 italic">
          No tasks in this schedule are linked to a budget line item yet. Open the budget’s
          <span class="font-medium text-gray-700">Earned Value</span> tab to start linking.
        </div>`
      return
    }

    this.bodyTarget.innerHTML = tasks.map(entry => {
      const t = entry.task
      const linkCount = (entry.links || []).length
      const linkRows = (entry.links || []).map(link => `
        <div class="flex items-center justify-between gap-2 py-1">
          <div class="min-w-0">
            <a href="${this._escape(link.budget_url)}"
               class="text-blue-700 hover:underline truncate block">
              ${this._escape(link.group_name)} › ${this._escape(link.line_code || "")} ${this._escape(link.line_name)}
            </a>
            <div class="text-[11px] text-gray-500">
              ${this._escape(link.budget_label)}
              ${Number(link.weight) !== 1 ? ` · weight × ${Number(link.weight).toFixed(2)}` : ""}
            </div>
          </div>
          <button type="button"
                  data-action="click->eva-schedule-panel#removeLink"
                  data-link-id-param="${link.id}"
                  class="text-gray-400 hover:text-red-600 p-1 cursor-pointer"
                  title="Unlink">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      `).join("")

      return `
        <div class="mb-4 border border-gray-200 rounded-lg">
          <div class="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div class="min-w-0">
              <div class="font-medium text-gray-800 truncate">${this._escape(t.name)}</div>
              <div class="text-[11px] text-gray-500">${Math.round(t.percent_done || 0)}% complete</div>
            </div>
            <span class="text-[11px] text-gray-500">${linkCount} link${linkCount === 1 ? "" : "s"}</span>
          </div>
          <div class="px-3 py-1 divide-y divide-gray-100">
            ${linkRows}
          </div>
        </div>
      `
    }).join("")
  }

  async _refreshBadge() {
    try {
      const resp = await this._ajaxJSON("GET", this.linksUrlValue)
      if (resp.ok) this._updateBadgeCount(resp.body?.tasks?.length || 0)
    } catch { /* badge is cosmetic */ }
  }

  _updateBadgeCount(count) {
    if (!this.hasBadgeTarget) return
    if (count > 0) {
      this.badgeTarget.textContent = String(count)
      this.badgeTarget.classList.remove("hidden")
    } else {
      this.badgeTarget.classList.add("hidden")
    }
  }

  // ── Shared helpers (mirror of budget_controller.js helpers) ────────

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _show(el) { el.classList.remove("hidden") }
  _hide(el) { el.classList.add("hidden") }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        schedule_id: this.scheduleIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }
}
